<script setup>
import { ref, computed, onMounted } from 'vue';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import MotorReservaCalendarAPI from 'dashboard/api/motorReservaCalendar';

const subTab = ref('grid');
const startDate = ref(toDateInputValue(new Date()));
const days = ref(20);
const showRestrictions = ref(false);
const hideRates = ref(false);
const isLoading = ref(false);
const grid = ref({ rooms: [], dates: [] });

// Form: Atualizar Preços
const priceForm = ref({
  start_date: '',
  end_date: '',
  weekdays: [0, 1, 2, 3, 4, 5, 6],
  room_rate_ids: [],
  occupancies: { 1: '', 2: '' },
});

// Form: Atualizar Restrições
const restrictForm = ref({
  start_date: '',
  end_date: '',
  weekdays: [0, 1, 2, 3, 4, 5, 6],
  room_rate_ids: [],
  stop_sale: false,
  min_stay: '',
});

// Form: Inventário
const inventoryForm = ref({
  start_date: '',
  end_date: '',
  weekdays: [0, 1, 2, 3, 4, 5, 6],
  room_ids: [],
  available_units: 0,
});

const weekdayLabels = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

const allRoomRates = computed(() => {
  // Build flat list of room×rate combinations from grid (or fetch directly)
  return grid.value.rooms.flatMap(room =>
    room.rates.map(rate => ({
      id: rate.id,
      label: `${room.name} → ${rate.name}`,
    }))
  );
});

function toDateInputValue(date) {
  const d = new Date(date);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function formatDayHeader(iso) {
  const d = new Date(iso + 'T00:00:00');
  const dayNames = ['DOM', 'SEG', 'TER', 'QUA', 'QUI', 'SEX', 'SÁB'];
  const monthNames = [
    'jan',
    'fev',
    'mar',
    'abr',
    'mai',
    'jun',
    'jul',
    'ago',
    'set',
    'out',
    'nov',
    'dez',
  ];
  return {
    weekday: dayNames[d.getDay()],
    day: d.getDate(),
    month: monthNames[d.getMonth()],
  };
}

function formatPrice(value) {
  const n = Number(value);
  if (Number.isNaN(n)) return '0';
  if (n === 0) return '0';
  return new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 }).format(n);
}

const loadGrid = async () => {
  isLoading.value = true;
  try {
    const { data } = await MotorReservaCalendarAPI.grid(
      startDate.value,
      days.value
    );
    grid.value = data;
  } catch (error) {
    useAlert(error?.message || 'Erro ao carregar calendário.');
  } finally {
    isLoading.value = false;
  }
};

const toggleWeekday = (form, day) => {
  const idx = form.weekdays.indexOf(day);
  if (idx >= 0) form.weekdays.splice(idx, 1);
  else form.weekdays.push(day);
};

const toggleArrayValue = (arr, value) => {
  const idx = arr.indexOf(value);
  if (idx >= 0) arr.splice(idx, 1);
  else arr.push(value);
};

const submitPrices = async () => {
  if (!priceForm.value.start_date || !priceForm.value.end_date) {
    useAlert('Preencha as datas.');
    return;
  }
  if (priceForm.value.room_rate_ids.length === 0) {
    useAlert('Selecione ao menos uma combinação Quarto/Tarifa.');
    return;
  }
  try {
    const { data } = await MotorReservaCalendarAPI.updatePrices(
      priceForm.value
    );
    useAlert(`${data.affected} entradas atualizadas.`);
    await loadGrid();
  } catch (error) {
    useAlert(error?.message || 'Erro ao atualizar preços.');
  }
};

const submitRestrictions = async () => {
  if (!restrictForm.value.start_date || !restrictForm.value.end_date) {
    useAlert('Preencha as datas.');
    return;
  }
  if (restrictForm.value.room_rate_ids.length === 0) {
    useAlert('Selecione ao menos uma combinação Quarto/Tarifa.');
    return;
  }
  try {
    const payload = { ...restrictForm.value };
    if (payload.min_stay === '') delete payload.min_stay;
    const { data } = await MotorReservaCalendarAPI.updateRestrictions(payload);
    useAlert(`${data.affected} entradas atualizadas.`);
    await loadGrid();
  } catch (error) {
    useAlert(error?.message || 'Erro ao atualizar restrições.');
  }
};

const submitInventory = async () => {
  if (!inventoryForm.value.start_date || !inventoryForm.value.end_date) {
    useAlert('Preencha as datas.');
    return;
  }
  if (inventoryForm.value.room_ids.length === 0) {
    useAlert('Selecione ao menos um quarto.');
    return;
  }
  try {
    const { data } = await MotorReservaCalendarAPI.updateInventory(
      inventoryForm.value
    );
    useAlert(`${data.affected} entradas atualizadas.`);
    await loadGrid();
  } catch (error) {
    useAlert(error?.message || 'Erro ao atualizar inventário.');
  }
};

onMounted(async () => {
  await loadGrid();
  // Pre-populate form date fields with the loaded range
  if (grid.value.start_date) priceForm.value.start_date = grid.value.start_date;
  if (grid.value.end_date) priceForm.value.end_date = grid.value.end_date;
  if (grid.value.start_date)
    restrictForm.value.start_date = grid.value.start_date;
  if (grid.value.end_date) restrictForm.value.end_date = grid.value.end_date;
  if (grid.value.start_date)
    inventoryForm.value.start_date = grid.value.start_date;
  if (grid.value.end_date) inventoryForm.value.end_date = grid.value.end_date;
});

// Filtro para combinações quarto/tarifa
const calendarSearch = ref('');

const filteredRoomRates = computed(() => {
  if (!calendarSearch.value.trim()) return allRoomRates.value;
  const q = calendarSearch.value.toLowerCase();
  return allRoomRates.value.filter(opt => opt.label.toLowerCase().includes(q));
});

const ic =
  'rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-800 dark:text-slate-100 focus:border-woot-500 focus:outline-none';
</script>

<template>
  <div class="flex flex-col gap-4">
    <div>
      <h3 class="text-base font-semibold text-slate-800 dark:text-slate-100">
        Calendário de Preços
      </h3>
      <p class="text-xs text-slate-500 dark:text-slate-400">
        Defina os valores e restrições por período, tarifa, quarto e número de
        hóspedes.
      </p>
    </div>

    <!-- Sub-abas -->
    <div class="flex gap-1 border-b border-slate-200 dark:border-slate-600">
      <button
        type="button"
        class="px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors uppercase tracking-wide"
        :class="
          subTab === 'grid'
            ? 'border-woot-500 text-woot-500'
            : 'border-transparent text-slate-500 hover:text-slate-700'
        "
        @click="subTab = 'grid'"
      >
        Grade
      </button>
      <button
        type="button"
        class="px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors uppercase tracking-wide"
        :class="
          subTab === 'prices'
            ? 'border-woot-500 text-woot-500'
            : 'border-transparent text-slate-500 hover:text-slate-700'
        "
        @click="subTab = 'prices'"
      >
        Atualizar Preços
      </button>
      <button
        type="button"
        class="px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors uppercase tracking-wide"
        :class="
          subTab === 'restrictions'
            ? 'border-woot-500 text-woot-500'
            : 'border-transparent text-slate-500 hover:text-slate-700'
        "
        @click="subTab = 'restrictions'"
      >
        Atualizar Restrições
      </button>
      <button
        type="button"
        class="px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors uppercase tracking-wide"
        :class="
          subTab === 'inventory'
            ? 'border-woot-500 text-woot-500'
            : 'border-transparent text-slate-500 hover:text-slate-700'
        "
        @click="subTab = 'inventory'"
      >
        Inventário
      </button>
    </div>

    <!-- ============== GRADE ============== -->
    <div v-show="subTab === 'grid'" class="flex flex-col gap-3">
      <div class="flex items-center gap-3 flex-wrap">
        <input v-model="startDate" type="date" :class="ic" />
        <input
          v-model.number="days"
          type="number"
          min="7"
          max="60"
          :class="ic"
          class="w-20"
          title="Quantidade de dias"
        />
        <Button label="CARREGAR" :is-loading="isLoading" @click="loadGrid" />
        <span class="ml-auto text-sm text-slate-500">Legenda:</span>
        <span class="text-xs text-red-500 line-through">10</span>
        <span class="text-xs text-slate-500">- Stop de vendas</span>
      </div>

      <div
        class="flex items-center gap-4 text-sm text-slate-600 dark:text-slate-300 px-1"
      >
        <a
          class="text-woot-500 underline cursor-pointer"
          @click="hideRates = !hideRates"
        >
          {{ hideRates ? 'mostrar tarifas' : 'ocultar tarifas' }}
        </a>
        <label class="flex items-center gap-1.5 cursor-pointer">
          <input
            v-model="showRestrictions"
            type="checkbox"
            class="rounded border-slate-300 text-woot-500"
          />
          restrições
        </label>
      </div>

      <div
        class="overflow-x-auto border border-slate-200 dark:border-slate-600 rounded-lg"
      >
        <table class="w-full text-sm border-collapse">
          <thead>
            <tr class="bg-woot-500 text-white">
              <th
                class="text-left p-3 sticky left-0 bg-woot-500 z-10 min-w-[260px]"
              >
                <span class="text-xs font-semibold uppercase">Quarto / Tarifa</span>
              </th>
              <th
                v-for="d in grid.dates || []"
                :key="d"
                class="text-center p-2 text-xs font-semibold whitespace-nowrap min-w-[58px]"
              >
                <div>{{ formatDayHeader(d).weekday }}</div>
                <div class="text-base">{{ formatDayHeader(d).day }}</div>
                <div>{{ formatDayHeader(d).month }}</div>
              </th>
            </tr>
          </thead>
          <tbody>
            <template v-for="room in grid.rooms" :key="room.id">
              <!-- Linha de inventário do quarto -->
              <tr
                class="bg-slate-50 dark:bg-slate-800 border-t border-slate-200 dark:border-slate-700"
              >
                <td
                  class="p-3 font-semibold text-slate-700 dark:text-slate-200 sticky left-0 bg-slate-50 dark:bg-slate-800 z-10 uppercase text-xs"
                >
                  {{ room.name }}
                </td>
                <td
                  v-for="d in grid.dates || []"
                  :key="`inv-${room.id}-${d}`"
                  class="text-center p-2 text-sm font-medium text-slate-700 dark:text-slate-200"
                >
                  {{ room.inventory_by_date[d] }}
                </td>
              </tr>
              <!-- Linhas de tarifas -->
              <template v-if="!hideRates">
                <tr
                  v-for="rate in room.rates"
                  :key="`rate-${rate.id}`"
                  class="border-t border-slate-100 dark:border-slate-700"
                >
                  <td class="p-2 sticky left-0 bg-white dark:bg-slate-900 z-10">
                    <div
                      class="text-xs font-medium uppercase text-slate-700 dark:text-slate-200 leading-tight"
                    >
                      {{ rate.name }}
                    </div>
                    <div class="text-[10px] text-slate-400">
                      <span
                        class="inline-block px-1 py-0.5 bg-woot-50 text-woot-600 rounded mr-1 mt-1"
                        >MOTOR</span>
                      <span class="text-[10px] text-slate-500">1 pessoa</span>
                      <br />
                      <span class="text-[10px] text-slate-500">2 pessoas</span>
                    </div>
                  </td>
                  <td
                    v-for="day in rate.days"
                    :key="`d-${rate.id}-${day.date}`"
                    class="text-center p-2 align-top"
                    :class="day.stop_sale ? 'bg-red-50 dark:bg-red-900/10' : ''"
                  >
                    <div
                      :class="
                        day.stop_sale
                          ? 'text-red-500 line-through text-xs'
                          : 'text-woot-500 text-xs font-medium'
                      "
                    >
                      {{ formatPrice(day.price_1) }}
                    </div>
                    <div
                      :class="
                        day.stop_sale
                          ? 'text-red-500 line-through text-xs'
                          : 'text-emerald-600 text-xs font-medium'
                      "
                    >
                      {{ formatPrice(day.price_2) }}
                    </div>
                    <div
                      v-if="showRestrictions && day.min_stay"
                      class="text-[10px] text-amber-600"
                    >
                      ≥{{ day.min_stay }}
                    </div>
                  </td>
                </tr>
              </template>
            </template>
            <tr v-if="!grid.rooms || grid.rooms.length === 0">
              <td
                :colspan="(grid.dates || []).length + 1"
                class="text-center p-8 text-slate-400"
              >
                Nenhum quarto cadastrado. Crie quartos e combinações
                Quarto/Tarifa primeiro.
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- ============== ATUALIZAR PREÇOS ============== -->
    <div v-show="subTab === 'prices'" class="flex flex-col gap-4 max-w-3xl">
      <div class="border border-slate-200 dark:border-slate-600 rounded-lg p-4">
        <h4
          class="text-sm font-semibold uppercase mb-3 text-slate-700 dark:text-slate-200"
        >
          Período e Dias da Semana
        </h4>
        <div class="grid grid-cols-2 gap-4 mb-3">
          <div>
            <label class="block text-xs text-slate-500 mb-1">Data Início</label>
            <input
              v-model="priceForm.start_date"
              type="date"
              :class="ic"
              class="w-full"
            />
          </div>
          <div>
            <label class="block text-xs text-slate-500 mb-1">Data Fim</label>
            <input
              v-model="priceForm.end_date"
              type="date"
              :class="ic"
              class="w-full"
            />
          </div>
        </div>
        <label class="block text-xs text-slate-500 mb-2">Dias da Semana</label>
        <div class="flex gap-2 flex-wrap">
          <button
            v-for="(label, idx) in weekdayLabels"
            :key="idx"
            type="button"
            class="px-3 py-1.5 text-xs font-semibold rounded-full border transition-colors"
            :class="
              priceForm.weekdays.includes(idx)
                ? 'bg-woot-500 text-white border-woot-500'
                : 'bg-white dark:bg-slate-700 text-slate-500 border-slate-200 dark:border-slate-600'
            "
            @click="toggleWeekday(priceForm, idx)"
          >
            {{ label }}
          </button>
        </div>
      </div>

      <div class="border border-slate-200 dark:border-slate-600 rounded-lg p-4">
        <h4
          class="text-sm font-semibold uppercase mb-3 text-slate-700 dark:text-slate-200"
        >
          Combinações Quarto / Tarifa
        </h4>
        <input
          v-model="calendarSearch"
          type="text"
          :class="ic"
          class="w-full mb-3"
          placeholder="Buscar quarto ou tarifa..."
        />
        <div v-if="allRoomRates.length === 0" class="text-xs text-slate-400">
          Nenhuma combinação encontrada. Carregue a Grade primeiro.
        </div>
        <div v-else class="grid grid-cols-2 gap-2 max-h-64 overflow-y-auto">
          <label
            v-for="opt in filteredRoomRates"
            :key="opt.id"
            class="flex items-center gap-2 text-xs text-slate-700 dark:text-slate-200 cursor-pointer"
          >
            <input
              type="checkbox"
              :checked="priceForm.room_rate_ids.includes(opt.id)"
              class="rounded border-slate-300 text-woot-500"
              @change="toggleArrayValue(priceForm.room_rate_ids, opt.id)"
            />
            {{ opt.label }}
          </label>
        </div>
      </div>

      <div class="border border-slate-200 dark:border-slate-600 rounded-lg p-4">
        <h4
          class="text-sm font-semibold uppercase mb-3 text-slate-700 dark:text-slate-200"
        >
          Valores por Ocupação
        </h4>
        <p class="text-xs text-slate-500 mb-3">
          Informe os valores em R$ por diária. Deixe em branco para não alterar.
        </p>
        <div class="grid grid-cols-2 gap-4">
          <div>
            <label class="block text-xs text-slate-500 mb-1">1 pessoa (R$)</label>
            <input
              v-model="priceForm.occupancies[1]"
              type="number"
              min="0"
              step="0.01"
              :class="ic"
              class="w-full"
              placeholder="Ex: 261.00"
            />
          </div>
          <div>
            <label class="block text-xs text-slate-500 mb-1">2 pessoas (R$)</label>
            <input
              v-model="priceForm.occupancies[2]"
              type="number"
              min="0"
              step="0.01"
              :class="ic"
              class="w-full"
              placeholder="Ex: 411.00"
            />
          </div>
        </div>
      </div>

      <div class="flex justify-end">
        <Button label="Aplicar Preços" @click="submitPrices" />
      </div>
    </div>

    <!-- ============== ATUALIZAR RESTRIÇÕES ============== -->
    <div
      v-show="subTab === 'restrictions'"
      class="flex flex-col gap-4 max-w-3xl"
    >
      <div class="border border-slate-200 dark:border-slate-600 rounded-lg p-4">
        <h4
          class="text-sm font-semibold uppercase mb-3 text-slate-700 dark:text-slate-200"
        >
          Período e Dias da Semana
        </h4>
        <div class="grid grid-cols-2 gap-4 mb-3">
          <div>
            <label class="block text-xs text-slate-500 mb-1">Data Início</label>
            <input
              v-model="restrictForm.start_date"
              type="date"
              :class="ic"
              class="w-full"
            />
          </div>
          <div>
            <label class="block text-xs text-slate-500 mb-1">Data Fim</label>
            <input
              v-model="restrictForm.end_date"
              type="date"
              :class="ic"
              class="w-full"
            />
          </div>
        </div>
        <label class="block text-xs text-slate-500 mb-2">Dias da Semana</label>
        <div class="flex gap-2 flex-wrap">
          <button
            v-for="(label, idx) in weekdayLabels"
            :key="idx"
            type="button"
            class="px-3 py-1.5 text-xs font-semibold rounded-full border transition-colors"
            :class="
              restrictForm.weekdays.includes(idx)
                ? 'bg-woot-500 text-white border-woot-500'
                : 'bg-white dark:bg-slate-700 text-slate-500 border-slate-200 dark:border-slate-600'
            "
            @click="toggleWeekday(restrictForm, idx)"
          >
            {{ label }}
          </button>
        </div>
      </div>

      <div class="border border-slate-200 dark:border-slate-600 rounded-lg p-4">
        <h4
          class="text-sm font-semibold uppercase mb-3 text-slate-700 dark:text-slate-200"
        >
          Combinações Quarto / Tarifa
        </h4>
        <input
          v-model="calendarSearch"
          type="text"
          :class="ic"
          class="w-full mb-3"
          placeholder="Buscar quarto ou tarifa..."
        />
        <div v-if="allRoomRates.length === 0" class="text-xs text-slate-400">
          Nenhuma combinação encontrada. Carregue a Grade primeiro.
        </div>
        <div v-else class="grid grid-cols-2 gap-2 max-h-64 overflow-y-auto">
          <label
            v-for="opt in filteredRoomRates"
            :key="opt.id"
            class="flex items-center gap-2 text-xs text-slate-700 dark:text-slate-200 cursor-pointer"
          >
            <input
              type="checkbox"
              :checked="restrictForm.room_rate_ids.includes(opt.id)"
              class="rounded border-slate-300 text-woot-500"
              @change="toggleArrayValue(restrictForm.room_rate_ids, opt.id)"
            />
            {{ opt.label }}
          </label>
        </div>
      </div>

      <div class="border border-slate-200 dark:border-slate-600 rounded-lg p-4">
        <h4
          class="text-sm font-semibold uppercase mb-3 text-slate-700 dark:text-slate-200"
        >
          Restrições
        </h4>
        <label
          class="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200 mb-3"
        >
          <input
            v-model="restrictForm.stop_sale"
            type="checkbox"
            class="rounded border-slate-300 text-woot-500"
          />
          Stop de vendas (bloquear venda nessas datas)
        </label>
        <div>
          <label class="block text-xs text-slate-500 mb-1">Estadia mínima (noites) — opcional</label>
          <input
            v-model.number="restrictForm.min_stay"
            type="number"
            min="1"
            :class="ic"
            class="w-32"
            placeholder="Ex: 2"
          />
        </div>
      </div>

      <div class="flex justify-end">
        <Button label="Aplicar Restrições" @click="submitRestrictions" />
      </div>
    </div>

    <!-- ============== INVENTÁRIO ============== -->
    <div v-show="subTab === 'inventory'" class="flex flex-col gap-4 max-w-3xl">
      <div class="border border-slate-200 dark:border-slate-600 rounded-lg p-4">
        <h4
          class="text-sm font-semibold uppercase mb-3 text-slate-700 dark:text-slate-200"
        >
          Período e Dias da Semana
        </h4>
        <div class="grid grid-cols-2 gap-4 mb-3">
          <div>
            <label class="block text-xs text-slate-500 mb-1">Data Início</label>
            <input
              v-model="inventoryForm.start_date"
              type="date"
              :class="ic"
              class="w-full"
            />
          </div>
          <div>
            <label class="block text-xs text-slate-500 mb-1">Data Fim</label>
            <input
              v-model="inventoryForm.end_date"
              type="date"
              :class="ic"
              class="w-full"
            />
          </div>
        </div>
        <label class="block text-xs text-slate-500 mb-2">Dias da Semana</label>
        <div class="flex gap-2 flex-wrap">
          <button
            v-for="(label, idx) in weekdayLabels"
            :key="idx"
            type="button"
            class="px-3 py-1.5 text-xs font-semibold rounded-full border transition-colors"
            :class="
              inventoryForm.weekdays.includes(idx)
                ? 'bg-woot-500 text-white border-woot-500'
                : 'bg-white dark:bg-slate-700 text-slate-500 border-slate-200 dark:border-slate-600'
            "
            @click="toggleWeekday(inventoryForm, idx)"
          >
            {{ label }}
          </button>
        </div>
      </div>

      <div class="border border-slate-200 dark:border-slate-600 rounded-lg p-4">
        <h4
          class="text-sm font-semibold uppercase mb-3 text-slate-700 dark:text-slate-200"
        >
          Quartos
        </h4>
        <div v-if="grid.rooms.length === 0" class="text-xs text-slate-400">
          Nenhum quarto encontrado. Carregue a Grade primeiro.
        </div>
        <div v-else class="grid grid-cols-2 gap-2 max-h-64 overflow-y-auto">
          <label
            v-for="r in grid.rooms"
            :key="r.id"
            class="flex items-center gap-2 text-xs text-slate-700 dark:text-slate-200 cursor-pointer"
          >
            <input
              type="checkbox"
              :checked="inventoryForm.room_ids.includes(r.id)"
              class="rounded border-slate-300 text-woot-500"
              @change="toggleArrayValue(inventoryForm.room_ids, r.id)"
            />
            {{ r.name }} ({{ r.total_units }} unidades)
          </label>
        </div>
      </div>

      <div class="border border-slate-200 dark:border-slate-600 rounded-lg p-4">
        <label class="block text-xs text-slate-500 mb-1">Unidades disponíveis por dia</label>
        <input
          v-model.number="inventoryForm.available_units"
          type="number"
          min="0"
          :class="ic"
          class="w-32"
        />
      </div>

      <div class="flex justify-end">
        <Button label="Aplicar Inventário" @click="submitInventory" />
      </div>
    </div>
  </div>
</template>
